import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { XIcon } from 'lucide-react';
import { products, collections, stories, sizes, giftThemes } from '../data/mockData';
import { ProductCard } from '../components/ProductCard';
type Sort = 'featured' | 'new' | 'best';
const CHIP_BASE = 'type-cta rounded-full border px-4 py-2.5 text-[10px] transition-colors';
export function Shop() {
  const [params, setParams] = useSearchParams();
  const [collection, setCollection] = useState<string | null>(null);
  const [story, setStory] = useState<string | null>(null);
  const [size, setSize] = useState<string | null>(null);
  const [gift, setGift] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>('featured');

  // Seed filters from the mega-menu and gifting deep links
  useEffect(() => {
    setCollection(params.get('collection'));
    setStory(params.get('story'));
    setSize(params.get('size'));
    setGift(params.get('gift'));
    const s = params.get('sort');
    setSort(s === 'new' || s === 'best' ? s : 'featured');
  }, [params]);
  const results = useMemo(() => {
    let list = products.filter(p => {
      if (collection && p.rangeId !== collection) return false;
      if (story && p.rangeId !== story) return false;
      if (size && p.size !== size) return false;
      if (gift && !p.giftThemes.includes(gift)) return false;
      return true;
    });
    if (sort === 'new') list = list.filter(p => p.newArrival);
    if (sort === 'best') list = list.filter(p => p.bestSeller);
    return list;
  }, [collection, story, size, gift, sort]);
  const activeCount = [collection, story, size, gift].filter(Boolean).length;
  const clearAll = () => {
    setCollection(null);
    setStory(null);
    setSize(null);
    setGift(null);
    setSort('featured');
    setParams({});
  };
  const giftTheme = giftThemes.find(g => g.id === gift);
  const heading = giftTheme ? `Gifts · ${giftTheme.title}` : sort === 'new' ? 'New Arrivals' : sort === 'best' ? 'Bestsellers' : 'Shop all cups';
  return <div className="w-full bg-paper" data-id="element-828" data-mp="2fx2ffd9oomgx">
      {/* Header */}
      <section className="bg-paper-warm" data-id="element-829" data-mp="2fyvkio4neymt">
        <div className="mx-auto max-w-[1400px] px-6 pb-12 pt-16 md:pb-16 md:pt-20" data-id="element-830" data-mp="2twk7c1ia0fui">
          <p className="type-eyebrow mb-5 text-[11px] text-pennant" data-id="element-831" data-mp="2owpevtiuri7c">Shop</p>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-832" data-mp="2ovedg0cvqmje">
            <h1 className="type-display text-5xl text-coffee md:text-6xl" data-id="element-833" data-mp="215a8npnyggqt">
              {heading}
            </h1>
            <p className="type-body text-sm text-ink/55" data-id="element-834" data-mp="2axg1t2py147l">
              {results.length} {results.length === 1 ? 'cup' : 'cups'} · 250ml,
              350ml &amp; 400ml
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[73px] z-30 border-b border-oat/50 bg-paper/95 backdrop-blur-md" data-id="element-835" data-mp="1ifken4s0nyzy">
        <div className="mx-auto max-w-[1400px] space-y-4 px-6 py-6" data-id="element-836" data-mp="1dhiuegiuttmw">
          <FilterRow label="Collection" data-id="element-837" data-mp="3p55jabrltocw">
            {collections.map(c => <Chip key={c.id} active={collection === c.id} onClick={() => setCollection(collection === c.id ? null : c.id)} data-id="element-838" data-mp="3twnnd8oxc523">
                {c.name}
              </Chip>)}
          </FilterRow>

          <FilterRow label="Choose Your Story" accent data-id="element-839" data-mp="bpzm5twzw21p">
            {stories.map(s => <Chip key={s.id} accent active={story === s.id} onClick={() => setStory(story === s.id ? null : s.id)} data-id="element-840" data-mp="gq41vhq2wsqv">
                {s.name}
              </Chip>)}
          </FilterRow>

          <FilterRow label="Size" id="size" data-id="element-841" data-mp="2ng1u55m79pty">
            {sizes.map(s => <Chip key={s.id} active={size === s.id} onClick={() => setSize(size === s.id ? null : s.id)} data-id="element-842" data-mp="2ni4uojkzb1cx">
                {s.label}
              </Chip>)}
          </FilterRow>

          {(activeCount > 0 || sort !== 'featured') && <button onClick={clearAll} className="type-cta inline-flex items-center gap-1.5 text-[10px] text-pennant" data-id="element-843" data-mp="32w8hvftitjj4">
              <XIcon className="h-3 w-3" strokeWidth={2} data-id="element-844" data-mp="2tyoea086rb5c" />
              Clear filters
            </button>}
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1400px] px-6 py-16 md:py-20" data-id="element-845" data-mp="3lo1erlbpnow4">
        {results.length > 0 ? <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-id="element-846" data-mp="3hm6duhxozdiv">
            {results.map((p, i) => <ProductCard key={p.id} product={p} index={i} data-id="element-847" data-mp="3cn37mu5z719b" data-mpi={i} />)}
          </div> : <div className="flex flex-col items-center py-24 text-center" data-id="element-848" data-mp="269dp7g077571">
            <p className="type-display text-4xl text-coffee" data-id="element-849" data-mp="218g52xvmo12w">
              Nothing matches that yet.
            </p>
            <p className="type-body mt-4 max-w-sm text-sm text-ink/60" data-id="element-850" data-mp="gryj7tyuviwj">
              Try loosening one filter.
            </p>
            <button onClick={clearAll} className="type-cta mt-8 rounded-full bg-coffee px-8 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-851" data-mp="lnnpsh9am02c">
              Clear filters
            </button>
          </div>}
      </section>
    </div>;
}
function FilterRow({
  label,
  accent,
  id,
  children
}: {
  label: string;
  accent?: boolean;
  id?: string;
  children: React.ReactNode;
}) {
  return <div id={id} className="flex scroll-mt-40 flex-wrap items-center gap-x-3 gap-y-2" data-id="element-852" data-mp="19ge06m1jsbai">
      <span className={`type-eyebrow mr-1 text-[9px] ${accent ? 'text-pennant' : 'text-coffee/45'}`} data-id="element-853" data-mp="1dfmrlk947x2w">
        {label}
      </span>
      {children}
    </div>;
}
function Chip({
  active,
  accent,
  onClick,
  children
}: {
  active: boolean;
  accent?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const activeCls = accent ? 'border-pennant bg-pennant text-paper' : 'border-coffee bg-coffee text-paper';
  const idleCls = accent ? 'border-pennant/30 text-pennant hover:border-pennant' : 'border-oat text-coffee/70 hover:border-coffee hover:text-coffee';
  return <button onClick={onClick} aria-pressed={active} className={`${CHIP_BASE} ${active ? activeCls : idleCls}`} data-id="element-854" data-mp="rf01ayeb6b09">
      {children}
    </button>;
}